import { Component, OnInit } from "@angular/core";
import { ActivatedRoute, Router } from "@angular/router";
import { ApiCallingService } from "../../services/api-calling.service";
import { ConfigApi } from "../../config-api";
import { ToastrService } from "ngx-toastr";
import { Globals } from "../../common/global";
import { NgbModal } from "@ng-bootstrap/ng-bootstrap";
import { PosOrderModalComponent } from "../pos-order-modal/pos-order-modal.component";

@Component({
  selector: "app-pos-order-details",
  templateUrl: "./pos-order-details.component.html",
  styleUrls: ["./pos-order-details.component.css"],
})
export class PosOrderDetailsComponent implements OnInit {
  orderDetails: any = {};
  loading: boolean = false;
  orderId: string = "";
  statusList = ["pending", "accepted", "completed", "cancelled"];
  getStatusLabel(status: string): string {
    if (!status) return "";

    return status
      .replace(/([A-Z])/g, " $1")
      .replace(/^./, (s) => s.toUpperCase());
  }
  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private apiCallingService: ApiCallingService,
    private toastr: ToastrService,
    public globals: Globals,
    private modalService: NgbModal,
  ) {}

  ngOnInit(): void {
    this.route.params.subscribe((params) => {
      this.orderId = params["id"];
      if (this.orderId) {
        this.getOrderDetails();
      }
    });
  }

  getOrderDetails(): void {
    this.loading = true;
    this.apiCallingService
      .callAPI({}, "GET", `${ConfigApi.URLS.POS.ORDER_DETAILS}${this.orderId}`)
      .subscribe(
        (res) => {
          this.loading = false;
          if (this.apiCallingService.handleATZSuccess(res)) {
            this.orderDetails = res.data;
          }
        },
        (error) => {
          this.loading = false;
          this.toastr.error("Failed to load order details");
        },
      );
  }

  goBack(): void {
    this.router.navigate(["/user/pos-orders"]);
  }

  getStatusBadgeClass(status: string): string {
    switch (status?.toLowerCase()) {
      case "pending":
        return "badge-warning";
      case "accepted":
        return "badge-info";
      case "readytopickup":
        return "badge-primary";
      case "completed":
        return "badge-success";
      case "cancelled":
        return "badge-danger";
      default:
        return "badge-secondary";
    }
  }

  getAvailableActions(): string[] {
    if (!this.orderDetails || !this.orderDetails.status) {
      return [];
    }

    const status = this.orderDetails.status;
    const actions = [];

    if (status === "pending") {
      actions.push("accept");
    } else if (status === "accepted") {
      actions.push("readyForPickup");
    } else if (status === "readyToPickup") {
      actions.push("complete");
    }

    if (["pending", "accepted", "readyToPickup"].includes(status)) {
      actions.push("cancel");
    }

    return actions;
  }

  openModal(actionType: string): void {
    const modalRef = this.modalService.open(PosOrderModalComponent, {
      centered: true,
      backdrop: "static",
    });

    const titleMap: { [key: string]: string } = {
      accept: "Accept Order",
      readyForPickup: "Ready for Pickup",
      complete: "Complete Order",
      cancel: "Cancel Order",
    };

    modalRef.componentInstance.title = titleMap[actionType] || "Order Action";
    modalRef.componentInstance.actionType = actionType;
    modalRef.componentInstance.order = this.orderDetails;
    modalRef.componentInstance.requiresReason = actionType === "cancel";

    modalRef.result.then(
      (result) => {
        if (result) {
          if (actionType === "cancel") {
            this.performAction("cancelled", result);
          } else {
            const nextStatusMap: { [key: string]: string } = {
              accept: "accepted",
              readyForPickup: "readyToPickup",
              complete: "completed",
            };
            this.performAction(nextStatusMap[actionType]);
          }
        }
      },
      () => {},
    );
  }

  performAction(status: string, reason?: string): void {
    const payload: any = { status };
    if (status === "cancelled" && reason) {
      payload.cancel_reason = reason;
    }

    this.loading = true;
    this.apiCallingService
      .callAPI(
        payload,
        "PUT",
        `${ConfigApi.URLS.POS.UPDATE_ORDER_STATUS}${this.orderId}`,
      )
      .subscribe(
        (res) => {
          this.loading = false;
          if (this.apiCallingService.handleATZSuccess(res)) {
            this.toastr.success(`Order status updated to ${status}`);
            this.getOrderDetails();
          }
        },
        (error) => {
          this.loading = false;
          this.toastr.error("Failed to update order status");
        },
      );
  }

  PrintDetails(order: any) {
    const popup = window.open("", "_blank", "width=1200,height=800");

    if (!popup) {
      this.toastr.error("Popup blocked. Please allow popups for this site.");
      return;
    }

    const html = this.generatePrintHTML(order);

    setTimeout(() => {
      popup.document.open();
      popup.document.write(html);
      popup.document.close();
    }, 150);
  }

  generatePrintHTML(order: any) {
    const productsHTML = (order?.productsItems ?? [])
      .map((p: any) => {
        return `
          <tr>
            <td>${p.productId?.name}</td>
            <td><img src="${
              p.productId?.productImage
            }" width="60" height="60" style="object-fit: cover; border-radius: 4px;" /></td>
            <td><b>${p.quantity}</b></td>
            <td>${p.productId?.EANNumber || "N/A"}</td>
            <td>${order.currency} ${Number(p.productId?.price ?? 0).toFixed(
              2,
            )}</td>
            <td>${p.totalVatPercentage}%</td>
            <td><b>${order.currency} ${Number(p.chargeableAmount ?? 0).toFixed(
              2,
            )}</b></td>
          </tr>
        `;
      })
      .join("");

    return `
    <html>
    <head>
      <title>Order ${order.orderNumber || order._id}</title>
      <style>
        body { font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif; padding: 30px; color: #333; }
        .header { display: flex; justify-content: space-between; align-items: center; border-bottom: 2px solid #eee; padding-bottom: 20px; margin-bottom: 30px; }
        .logo-section h2 { margin: 0; color: #1e88e5; font-size: 28px; }
        .order-info-section { display: flex; justify-content: space-between; margin-bottom: 30px; }
        .info-box { flex: 1; }
        .info-box h3 { margin-top: 0; font-size: 18px; border-bottom: 1px solid #eee; padding-bottom: 10px; margin-bottom: 15px; text-transform: uppercase; color: #666; }
        .info-item { margin-bottom: 8px; font-size: 14px; }
        .info-item b { width: 140px; display: inline-block; color: #555; }
        table { width: 100%; border-collapse: collapse; margin-top: 20px; }
        th, td { border: 1px solid #eee; padding: 12px; text-align: left; font-size: 14px; }
        th { background-color: #f8f9fa; color: #333; font-weight: 600; text-transform: uppercase; font-size: 12px; }
        .total-section { margin-top: 30px; display: flex; justify-content: flex-end; }
        .total-box { width: 300px; background: #f8f9fa; padding: 20px; border-radius: 8px; }
        .total-row { display: flex; justify-content: space-between; margin-bottom: 10px; font-size: 14px; }
        .total-row.grand-total { border-top: 1px solid #ddd; pt-10; margin-top: 10px; font-weight: bold; font-size: 18px; color: #d9534f; }
        @media print {
          body { padding: 0; }
          .no-print { display: none; }
        }
      </style>
    </head>
    <body onload="window.print(); window.close();">
      <div class="header">
        <div class="logo-section">
          <h2>Order Details</h2>
          <div style="color: #888; font-size: 14px;">Order Number: ${
            order.orderNumber || order._id
          }</div>
        </div>
        <div class="date-section" style="text-align: right;">
          <div style="font-weight: 600;">Date:</div>
          <div>${new Date(order.created_at).toLocaleString()}</div>
        </div>
      </div>

      <div class="order-info-section">
        <div class="info-box">
          <h3>Order Information</h3>
          <div class="info-item"><b>Status:</b> ${order.status}</div>
          <div class="info-item"><b>Store:</b> ${
            order.operatorStoreId?.storeName || "N/A"
          }</div>
          <div class="info-item"><b>Desk:</b> ${
            order.deskId?.deskName || "N/A"
          }</div>
          <div class="info-item"><b>Payment Type:</b> ${
            order.paymentType || "N/A"
          }</div>
        </div>
        <div class="info-box">
          <h3>Customer Information</h3>
          <div class="info-item"><b>Name:</b> ${order.userName || "N/A"}</div>
          <div class="info-item"><b>Mobile:</b> ${
            order.userDiallingCode || ""
          } ${order.userMobileNumber || "-"}</div>
        </div>
      </div>

      <h3>Products</h3>
      <table>
        <thead>
          <tr>
            <th>Product Name</th>
            <th>Image</th>
            <th>Qty</th>
            <th>EAN</th>
            <th>Unit Price</th>
            <th>VAT %</th>
            <th>Total</th>
          </tr>
        </thead>
        <tbody>${productsHTML}</tbody>
      </table>

      <div class="total-section">
        <div class="total-box">
          <div class="total-row">
            <span>Subtotal:</span>
            <span>${order.currency} ${Number(
              order.totalAmountWithoutVat || 0,
            ).toFixed(2)}</span>
          </div>
          <div class="total-row">
            <span>VAT:</span>
            <span>${order.currency} ${Number(order.totalVatAmount || 0).toFixed(
              2,
            )}</span>
          </div>
          <div class="total-row grand-total">
            <span>Total Amount:</span>
            <span>${order.currency} ${Number(
              order.totalAmountWithVat || 0,
            ).toFixed(2)}</span>
          </div>
        </div>
      </div>
      
     
    </body>
    </html>
  `;
  }
}
